<template>
  <!-- The "More" toggle and its menu (JDEV-3207). Two root elements on purpose: both must be children of the
       .j-tabs-bar, which positions the menu (right-aligned) and pins the toggle to its inline end. -->
  <button
    ref="toggleEl"
    class="j-tabs-more"
    type="button"
    aria-haspopup="menu"
    :aria-expanded="isOpen ? 'true' : 'false'"
    :aria-controls="menuId"
    :aria-label="ariaLabel"
    :hidden="!items.length"
    v-on:click.stop="onToggleClick"
    v-on:keydown="onToggleKeydown"
  >
    <slot name="toggle" :count="items.length" :open="isOpen">{{ label }}</slot>
    <span class="j-tabs-more-count" aria-hidden="true">{{ items.length }}</span>
    <span class="caret" aria-hidden="true"></span>
  </button>
  <ul
    ref="menuEl"
    :id="menuId"
    class="dropdown-menu j-tabs-menu"
    role="menu"
    :aria-label="label"
    :hidden="!isOpen"
    v-on:keydown="onMenuKeydown"
    v-on:click.stop
  >
    <li v-for="item in items" :key="item.id" role="none" class="j-tabs-menu-row">
      <button
        type="button"
        role="menuitemradio"
        tabindex="-1"
        :aria-checked="item.id === checkedId ? 'true' : 'false'"
        :data-menu-id="item.id"
        v-on:click="choose(item.id)"
      >
        <slot name="item" :item="item" :checked="item.id === checkedId">{{ item.label }}</slot>
      </button>
      <!-- Extra controls for the row (e.g. a close button). Siblings of the menuitem, never inside it. -->
      <slot name="item-after" :item="item" :checked="item.id === checkedId" :close="close"></slot>
    </li>
  </ul>
</template>

<script setup lang="ts">
// Menu button for priority+ tab strips (useTabOverflow), markup and classes from shared/styles tabs.css:
// button.j-tabs-more[aria-haspopup=menu][aria-expanded] + ul.dropdown-menu.j-tabs-menu[role=menu] of
// menuitemradio rows. The menu is open while it has no hidden attribute.
//
// Props:   items [{ id, label }] (the hidden tabs, in order), checkedId (the selected tab; aria-checked),
//          label (visible text, e.g. "Lisää"), ariaLabel (e.g. "Lisää, 6 välilehteä piilossa"), menuId.
// Events:  select(id) when a row is chosen (the menu has closed; the parent activates the tab and moves focus
//          to it), open, close.
// Slots:   toggle { count, open } (the text before the count; default: label), item { item, checked } (the
//          menuitem's content; default: the label), item-after { item, checked, close } (siblings of the
//          menuitem in the row, e.g. a close button).
// Exposes: toggleEl (for the width reservation in useTabOverflow), open(focus), close(refocus), isOpen.
//
// Keys: on the toggle ArrowDown/Enter/Space open on the first row, ArrowUp on the last. In the menu
// ArrowUp/Down (wrapping), Home/End, type-ahead by first letter, Esc closes and returns focus to the toggle,
// Tab closes. A pointer press outside closes it. Rows activate on click (and Enter/Space), never on mousedown.

import { nextTick, onBeforeUnmount, ref, watch } from 'vue'

export interface TabOverflowItem {
  id: string
  label: string
}

const props = withDefaults(defineProps<{
  items: TabOverflowItem[]
  checkedId?: string | null
  label: string
  ariaLabel: string
  menuId: string
}>(), { checkedId: null })

const emit = defineEmits<{
  (e: 'select', id: string): void
  (e: 'open'): void
  (e: 'close'): void
}>()

const toggleEl = ref<HTMLButtonElement | null>(null)
const menuEl = ref<HTMLUListElement | null>(null)
const isOpen = ref(false)

const rows = () => Array.from(menuEl.value?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') || [])

function focusRow (index: number) {
  const all = rows()
  if (!all.length) return
  all[(index + all.length) % all.length].focus()
}

function onPointerDownOutside (e: PointerEvent) {
  const t = e.target as Node | null
  if (t && (toggleEl.value?.contains(t) || menuEl.value?.contains(t))) return
  close(false)
}

async function open (focus: 'first' | 'last' = 'first') {
  if (!props.items.length) return
  if (!isOpen.value) {
    isOpen.value = true
    document.addEventListener('pointerdown', onPointerDownOutside, true)
    emit('open')
  }
  await nextTick()
  focusRow(focus === 'first' ? 0 : -1)
}

function close (refocus = false) {
  if (isOpen.value) {
    isOpen.value = false
    document.removeEventListener('pointerdown', onPointerDownOutside, true)
    emit('close')
  }
  if (refocus) toggleEl.value?.focus()
}

function onToggleClick () {
  if (isOpen.value) close(true)
  else open('first')
}

function onToggleKeydown (e: KeyboardEvent) {
  if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
    // preventDefault: no synthetic click as well, which would toggle it shut again.
    e.preventDefault()
    open('first')
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    open('last')
  }
}

function onMenuKeydown (e: KeyboardEvent) {
  const all = rows()
  const k = all.indexOf(document.activeElement as HTMLElement)
  if (e.key === 'ArrowDown') { e.preventDefault(); focusRow(k + 1) }
  else if (e.key === 'ArrowUp') { e.preventDefault(); focusRow(k < 0 ? -1 : k - 1) }
  else if (e.key === 'Home') { e.preventDefault(); focusRow(0) }
  else if (e.key === 'End') { e.preventDefault(); focusRow(-1) }
  else if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); e.stopPropagation(); close(true) }
  else if (e.key === 'Tab') close(false)
  else if (e.key.length === 1 && /\S/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
    // Type-ahead: the next row (after the focused one, wrapping) whose label starts with the key.
    const ch = e.key.toLocaleLowerCase()
    const starts = (i: number) => (props.items[i]?.label || '').trim().toLocaleLowerCase().startsWith(ch)
    for (let n = 1; n <= all.length; n++) {
      const i = (Math.max(k, -1) + n) % all.length
      if (starts(i)) { e.preventDefault(); focusRow(i); break }
    }
  }
}

function choose (id: string) {
  close(false)
  emit('select', id)
  // The parent normally focuses the activated tab; if focus was left on the hidden menu, give it to the toggle.
  nextTick(() => requestAnimationFrame(() => {
    const active = document.activeElement
    if (!active || active === document.body || menuEl.value?.contains(active)) {
      if (toggleEl.value && !toggleEl.value.hidden) toggleEl.value.focus()
    }
  }))
}

watch(() => props.items.length, (n) => { if (!n) close(false) })

onBeforeUnmount(() => document.removeEventListener('pointerdown', onPointerDownOutside, true))

defineExpose({ toggleEl, open, close, isOpen })
</script>
